import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Avatar,
  Divider,
} from '@mui/material';
import { toast } from 'react-toastify';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { addNote, deleteNote, updateNote } from './api/underwritingApi';
import { useRolePermissions } from './context/RolePermissionsContext';
import { RestrictedAction, RestrictedRegion } from './components/RestrictedAction';

const sanitizeNoteContent = (content = '') =>
  DOMPurify.sanitize(content)
    .replace(/&nbsp;|&#160;|&#x0*a0;/gi, ' ')
    .replace(/\u00a0/g, ' ');

const UserNotes = ({ company, tenantId, onNotesUpdate, id, userInfo }) => {
  const { permissions } = useRolePermissions();
  const [newNote, setNewNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editedContent, setEditedContent] = useState('');

  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'], 
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ],
  };

  const handleAddNote = async () => {
    // ReactQuill returns HTML, so we check if text content exists (stripping tags)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = newNote;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";

    if (!textContent.trim()) {
      toast.warning('Please enter a note before adding');
      return;
    }

    try {
      setIsSaving(true);
      const updatedNotes = await addNote(tenantId, company.companyId, newNote);
      if (onNotesUpdate) {
        onNotesUpdate(updatedNotes.data);
      }
      setNewNote('');
      toast.success('Note added successfully');
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const updatedNotes = await deleteNote(tenantId, company.companyId, noteId);
      if (onNotesUpdate) {
        onNotesUpdate(updatedNotes.data);
      }
      toast.success('Note deleted successfully');
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note. Please try again.');
    }
  };

  const handleUpdateNote = async (noteId) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = editedContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";

    if (!textContent.trim()) {
      toast.warning('Note content cannot be empty');
      return;
    }

    try {
      setIsSaving(true);
      const updatedNotes = await updateNote(tenantId, company.companyId, noteId, editedContent);
      if (onNotesUpdate) {
        onNotesUpdate(updatedNotes.data);
      }
      setEditingNoteId(null);
      setEditedContent('');
      toast.success('Note updated successfully');
    } catch (error) {
      console.error('Error updating note:', error);
      toast.error('Failed to update note. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const startEditing = (note) => {
    setEditingNoteId(note.noteId);
    setEditedContent(note.content);
  };

  const cancelEditing = () => {
    setEditingNoteId(null);
    setEditedContent('');
  };

  const notes = company?.userNotes || [];

  return (
    <Box id={id} sx={{ mb: 3, mt: 3, scrollMarginTop: '70px' }}>
      <Typography
        sx={{
          fontSize: '20px',
          color: '#1A2A56',
          fontWeight: '600',
          mb: 2,
        }}>
        User Notes
      </Typography>

      <Paper
        sx={{
          background: '#FAFAFC',
          boxShadow: 'inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029',
          borderRadius: '8px',
          p: 3,
        }}>
        <Box sx={{ mb: 2 }}>
          <RestrictedRegion allowed={permissions.canAddNotes}>
          <Box sx={{ backgroundColor: 'white' }}>
            <ReactQuill
              theme="snow"
              value={newNote}
              onChange={setNewNote}
              modules={modules}
              placeholder="Add a new note..."
              readOnly={isSaving || !permissions.canAddNotes}
            />
          </Box>
          </RestrictedRegion>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <RestrictedAction allowed={permissions.canAddNotes}>
            <Button
              onClick={handleAddNote}
              disabled={isSaving}
              variant="contained"
              sx={{
                backgroundColor: '#5186EC',
                textTransform: 'none',
                fontFamily: 'Rubik, sans-serif',
                fontWeight: '250',
                '&:hover': {
                  backgroundColor: '#4070D0',
                },
              }}>
              {isSaving ? 'Adding...' : 'Add Note'}
            </Button>
            </RestrictedAction>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {notes.length > 0 ? (
          notes.map((note) => (
            <Box
              key={note.noteId}
              sx={{ display: 'flex', mb: 2, minWidth: 0, maxWidth: '100%' }}
            >
              <Avatar sx={{ width: 40, height: 40, mr: 2 }}>
                {note.authorName ? note.authorName.charAt(0) : '?'}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0, maxWidth: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {note.authorName || 'Unknown'}
                  </Typography>
                  {userInfo && (userInfo.id === note.authorId || userInfo.user_metadata?.isAdmin) && (
                    <Box>
                      <RestrictedAction allowed={permissions.canAddNotes}>
                      <IconButton
                        size="small"
                        onClick={() => startEditing(note)}
                        disabled={editingNoteId === note.noteId}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      </RestrictedAction>
                      <RestrictedAction allowed={permissions.canAddNotes}>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteNote(note.noteId)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      </RestrictedAction>
                    </Box>
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {new Date(note.createdAt).toLocaleString([], { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  {note.updatedAt && (
                    <em> (edited {new Date(note.updatedAt).toLocaleString([], { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })})</em>
                  )}
                </Typography>
                {editingNoteId === note.noteId ? (
                  <Box>
                    <Box sx={{ backgroundColor: 'white', mt: 1 }}>
                      <ReactQuill 
                        theme="snow" 
                        value={editedContent} 
                        onChange={setEditedContent}
                        modules={modules}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
                      <Button onClick={cancelEditing} size="small">Cancel</Button>
                      <Button onClick={() => handleUpdateNote(note.noteId)} size="small" variant="contained">Save</Button>
                    </Box>
                  </Box>
                ) : (
                  <Box 
                    sx={{ 
                      mt: 1,
                      minWidth: 0,
                      maxWidth: '100%',
                      overflowX: 'hidden',
                      overflowWrap: 'break-word',
                      wordBreak: 'normal',
                      '& p': { marginBottom: '0.5em' },
                      '& ul, & ol': { paddingLeft: '20px' },
                      '& p, & li, & div, & span, & a': {
                        maxWidth: '100%',
                        whiteSpace: 'normal !important',
                        overflowWrap: 'break-word',
                        wordBreak: 'normal',
                      },
                      '& pre': {
                        maxWidth: '100%',
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                      },
                      '& img, & table': { maxWidth: '100%' },
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitizeNoteContent(note.content) }}
                  />
                )}
              </Box>
            </Box>
          ))
        ) : (
          <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>
            No notes yet.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default UserNotes;
